import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Globe, LogOut, ChevronDown, Loader2, Sun, Moon } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useLogout } from '@/api/hooks/useAuth';
import { SUPPORTED_LANGUAGES } from '@/i18n/i18n';
import type { SupportedLanguage } from '@/i18n/i18n';

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains('dark'),
  );

  const toggle = () => {
    const html = document.documentElement;
    if (html.classList.contains('dark')) {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  // Sync state if class changes externally
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return { isDark, toggle };
}

export function Header() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogout();
  const { isDark, toggle: toggleDarkMode } = useDarkMode();

  const changeLanguage = (lng: SupportedLanguage) => {
    i18n.changeLanguage(lng);
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const initials = user?.display_name
    ? user.display_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? 'U';

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-gray-200 bg-white/80 px-6 backdrop-blur-md dark:border-surface-border dark:bg-surface-sidebar/80">
      {/* Left: page context */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-600">
          {t('app.name')}
        </span>
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1">

        {/* Dark / Light toggle */}
        <button
          onClick={toggleDarkMode}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-surface-hover dark:hover:text-gray-200"
          aria-label="Toggle theme"
        >
          {isDark ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </button>

        {/* Language switcher */}
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-surface-hover dark:hover:text-gray-300">
            <Globe className="h-4 w-4" />
            <span>{i18n.language.substring(0, 2)}</span>
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute end-0 mt-2 w-36 origin-top-end rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-surface-border dark:bg-surface-elevated dark:shadow-black/30">
              {SUPPORTED_LANGUAGES.map((lng) => (
                <MenuItem key={lng}>
                  {({ focus }) => (
                    <button
                      onClick={() => changeLanguage(lng)}
                      className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${
                        focus ? 'bg-gray-50 dark:bg-surface-hover' : ''
                      } ${
                        i18n.language === lng
                          ? 'font-semibold text-primary-600 dark:text-primary-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {t(`language.${lng}`)}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </Menu>

        {/* Divider */}
        <div className="mx-1 h-5 w-px bg-gray-200 dark:bg-surface-border" />

        {/* User menu */}
        <Menu as="div" className="relative">
          <MenuButton className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-surface-hover">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-600 ring-1 ring-primary-200 dark:bg-primary-500/20 dark:text-primary-400 dark:ring-primary-500/30">
              {initials}
            </div>
            <span className="hidden text-sm font-medium text-gray-700 dark:text-gray-300 sm:block">
              {user?.first_name || user?.display_name || t('auth.user')}
            </span>
            <ChevronDown className="hidden h-3.5 w-3.5 text-gray-400 sm:block" />
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute end-0 mt-2 w-52 origin-top-end rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-surface-border dark:bg-surface-elevated dark:shadow-black/30">
              {user?.email && (
                <div className="border-b border-gray-100 px-4 py-3 dark:border-surface-border">
                  <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                    {user.display_name || `${user.first_name} ${user.last_name}`}
                  </p>
                  <p className="truncate text-xs text-gray-500 mt-0.5">
                    {user.email}
                  </p>
                </div>
              )}
              <div className="py-1">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={handleLogout}
                      disabled={logoutMutation.isPending}
                      className={`flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 transition-colors dark:text-red-400 ${
                        focus ? 'bg-gray-50 dark:bg-surface-hover' : ''
                      }`}
                    >
                      {logoutMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <LogOut className="h-4 w-4" />
                      )}
                      {t('nav.logout')}
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
